import { useMemo, useState, type ReactNode } from 'react'
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ThemeProvider, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import CssBaseline from '@mui/material/CssBaseline'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Drawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import CircularProgress from '@mui/material/CircularProgress'
import MenuIcon from '@mui/icons-material/Menu'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import { buildTheme } from '../shared/theme'
import { useThemeMode } from '../shared/themeMode'
import ModeToggle from '../shared/components/ModeToggle'
import { useShop } from '../shared/api/storefront'
import { ConfirmProvider } from '../shared/components/ConfirmDialog'
import { OwnerAuthProvider, useOwnerAuth } from './OwnerAuthContext'
import BoardPage from './BoardPage'
import DashboardPage from './DashboardPage'
import ProductsPage from './ProductsPage'
import OrderHistoryPage from './OrderHistoryPage'
import StaffPage from './StaffPage'
import SettingsPage from './SettingsPage'
import ProfilePage from './ProfilePage'
import Chip from '@mui/material/Chip'
import { isOwnerLevel } from './roles'

// The owner-console navigation (§16 Phase 6). `ownerOnly` tabs (staff management) show only
// to OWNER sessions — STAFF can't reach the backend endpoints anyway (OWNER-gated, §16 6c).
const NAV: { labelKey: string; path: string; ownerOnly?: boolean }[] = [
  { labelKey: 'owner.nav.dashboard', path: '/manage/dashboard', ownerOnly: true },
  { labelKey: 'owner.nav.orders', path: '/manage' },
  { labelKey: 'owner.nav.history', path: '/manage/orders' },
  { labelKey: 'owner.nav.products', path: '/manage/products', ownerOnly: true },
  { labelKey: 'owner.nav.staff', path: '/manage/staff', ownerOnly: true },
  { labelKey: 'owner.nav.settings', path: '/manage/settings', ownerOnly: true },
  { labelKey: 'owner.nav.profile', path: '/manage/profile' },
]

/** The owner console, mounted by App for any `/manage` path. Owns its own routes, theme and auth. */
export default function OwnerShell() {
  // Same farm-fresh design language as the storefront, tinted by the shop's brand_color (D-074).
  // Reuses the public useShop() (works for STAFF too, unlike the OWNER-only useShopSettings);
  // buildTheme(null) falls back to the default brand until the fetch resolves.
  const { data: shop } = useShop()
  const mode = useThemeMode()
  const theme = useMemo(() => buildTheme(shop?.brandColor, mode), [shop?.brandColor, mode])
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <OwnerAuthProvider>
        <ConfirmProvider>
        <Routes>
          {/* The owner signs in at the shop's single front-door /login now (D-066); keep this
              path as a redirect so old bookmarks and the onboarding runbook still land right. */}
          <Route path="/manage/login" element={<Navigate to="/login" replace />} />
          <Route
            path="/manage"
            element={
              <RequireStaff>
                <BoardPage />
              </RequireStaff>
            }
          />
          <Route
            path="/manage/dashboard"
            element={
              <RequireStaff ownerOnly>
                <DashboardPage />
              </RequireStaff>
            }
          />
          <Route
            path="/manage/orders"
            element={
              <RequireStaff>
                <OrderHistoryPage />
              </RequireStaff>
            }
          />
          <Route
            path="/manage/products"
            element={
              <RequireStaff ownerOnly>
                <ProductsPage />
              </RequireStaff>
            }
          />
          <Route
            path="/manage/staff"
            element={
              <RequireStaff ownerOnly>
                <StaffPage />
              </RequireStaff>
            }
          />
          <Route
            path="/manage/settings"
            element={
              <RequireStaff ownerOnly>
                <SettingsPage />
              </RequireStaff>
            }
          />
          <Route
            path="/manage/profile"
            element={
              <RequireStaff>
                <ProfilePage />
              </RequireStaff>
            }
          />
          <Route path="/manage/*" element={<Navigate to="/manage" replace />} />
        </Routes>
        </ConfirmProvider>
      </OwnerAuthProvider>
    </ThemeProvider>
  )
}

/**
 * Gates the console behind a staff session; unauthenticated → the sign-in page. With
 * `ownerOnly`, a non-OWNER staff member is bounced to the board (defence in depth — the
 * backend OWNER-gates these endpoints regardless, §16 6c).
 */
function RequireStaff({ children, ownerOnly }: { children: ReactNode; ownerOnly?: boolean }) {
  const { isAuthenticated, initializing, session } = useOwnerAuth()
  if (initializing) {
    // The boot silent refresh is restoring the session (D-053) — don't bounce to the
    // sign-in page while it's in flight.
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  if (ownerOnly && !isOwnerLevel(session?.role)) {
    return <Navigate to="/manage" replace />
  }
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Header />
      {children}
    </Box>
  )
}

function Header() {
  const { logout, session } = useOwnerAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [drawerOpen, setDrawerOpen] = useState(false)
  // OWNER-only tabs (Staff) are hidden from STAFF sessions; IT_ADMIN sees them (D-107).
  const tabs = NAV.filter((n) => !n.ownerOnly || isOwnerLevel(session?.role))
  // Highlight the most specific matching nav item ("/manage/products" before "/manage").
  const active =
    [...tabs].sort((a, b) => b.path.length - a.path.length).find((n) => pathname.startsWith(n.path))?.path ??
    '/manage'

  // Mobile: a burger opens a drawer with the full menu; an "Orders" shortcut stays in the bar
  // for one-tap access to the live board without opening the drawer.
  if (isMobile) {
    const go = (path: string) => {
      navigate(path)
      setDrawerOpen(false)
    }
    return (
      <>
        <AppBar position="sticky" color="primary" elevation={1}>
          <Toolbar sx={{ gap: 1 }}>
            <IconButton color="inherit" edge="start" aria-label={t('owner.openMenu')} onClick={() => setDrawerOpen(true)}>
              <MenuIcon />
            </IconButton>
            <Button color="inherit" onClick={() => navigate('/manage')} sx={{ fontWeight: 700 }}>
              {t('owner.nav.orders')}
            </Button>
            <AdminShopBadge />
            <Box sx={{ flexGrow: 1 }} />
            <ModeToggle />
            {/* <LanguageSwitcher /> — hidden for now; see StorefrontLayout. */}
          </Toolbar>
        </AppBar>
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
          <Box sx={{ width: 240 }} role="presentation">
            <Typography variant="h6" sx={{ fontWeight: 700, px: 2, pt: 2 }}>
              {t('owner.manage')}
            </Typography>
            {session && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }} noWrap>
                {session.name || session.email} · {session.role}
              </Typography>
            )}
            <Divider />
            <List>
              {tabs.map((n) => (
                <ListItemButton key={n.path} selected={active === n.path} onClick={() => go(n.path)}>
                  <ListItemText primary={t(n.labelKey)} />
                </ListItemButton>
              ))}
            </List>
            <Divider />
            <List>
              <ListItemButton onClick={logout}>
                <ListItemText primary={t('common.logOut')} />
              </ListItemButton>
            </List>
          </Box>
        </Drawer>
      </>
    )
  }

  return (
    <AppBar position="sticky" color="primary" elevation={1}>
      <Toolbar sx={{ gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {t('owner.manage')}
        </Typography>
        <AdminShopBadge />
        <Tabs
          value={active}
          onChange={(_, value) => navigate(value)}
          textColor="inherit"
          indicatorColor="secondary"
          sx={{ flexGrow: 1, minHeight: 0 }}
        >
          {tabs.map((n) => (
            <Tab key={n.path} value={n.path} label={t(n.labelKey)} sx={{ minHeight: 48 }} />
          ))}
        </Tabs>
        <ModeToggle />
        {/* <LanguageSwitcher /> — hidden for now; see StorefrontLayout. */}
        {/* Who's signed in — the staff member's name (falling back to email) + role (§9). */}
        {session && (
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mr: 1, minWidth: 0 }}>
            <AccountCircleIcon fontSize="small" />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, lineHeight: 1.15 }}>
                {session.name || session.email}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1 }}>
                {session.role}
              </Typography>
            </Box>
          </Stack>
        )}
        <Button color="inherit" onClick={logout}>
          {t('common.logOut')}
        </Button>
      </Toolbar>
    </AppBar>
  )
}

/**
 * A loud reminder of WHICH shop a platform admin (D-107) is currently inside. An IT_ADMIN moves
 * between tenants by changing host, and the console otherwise looks the same everywhere apart
 * from the brand tint — so the one fact worth shouting is the shop's name. Renders for nobody else.
 */
function AdminShopBadge() {
  const { session } = useOwnerAuth()
  const { t } = useTranslation()
  // Same query key as OwnerShell's useShop(), so TanStack serves it from cache — no extra request.
  const { data: shop } = useShop()
  if (session?.role !== 'IT_ADMIN') {
    return null
  }
  return (
    <Chip
      size="small"
      color="warning"
      label={t('owner.adminAt', { shop: shop?.name ?? '…' })}
      sx={{ fontWeight: 700, maxWidth: 220 }}
    />
  )
}
